import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { formatUsd } from '../../lib/format';
import { cn } from '@siliconx/shared/ui/utils';

export interface AssetOpt {
  id: string;
  serialNumber: string | null;
  assetClass: string;
  manufacturerName: string | null;
  modelName: string | null;
  fmvMedianUsd: number | null;
  teamId: string | null;
}

/** SIL-2544: shown on a non-server row when the target lot is a rack. The API still refuses direct calls. */
export const RACK_SERVERS_ONLY_REASON = 'Racks hold servers only';

/** Rack lots hold servers only (SIL-2539): a non-server asset cannot be added to one. */
export function isRackBlocked(asset: Pick<AssetOpt, 'assetClass'>, rackOnly: boolean): boolean {
  return rackOnly && asset.assetClass !== 'server';
}

// "Dell PowerEdge R750" when make/model are known, else the class - the serial
// alone was not a usable identifier in the old dropdown.
function assetLabel(a: AssetOpt): string {
  return [a.manufacturerName, a.modelName].filter(Boolean).join(' ') || a.assetClass;
}

/**
 * Searchable asset picker - replaces the old serial-only <select>. Typeahead
 * filters over make/model/serial/class; each row leads with a human label
 * ("Dell PowerEdge R750") and shows serial / class / FMV underneath so the
 * analyst can actually tell two assets apart. Same combobox conventions as
 * MakeModelPicker (keyboard nav, mousedown-before-blur).
 *
 * rackOnly (SIL-2544): the target lot is a rack. Non-server rows stay visible
 * but disabled with "Racks hold servers only"; keyboard nav skips them.
 */
export function LotAssetPicker({
  assets, value, onChange, disabled, rackOnly = false,
}: {
  assets: AssetOpt[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  rackOnly?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const hiRef = useRef<HTMLButtonElement | null>(null);

  const selected = value ? assets.find((a) => a.id === value) ?? null : null;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? assets.filter((a) =>
        [a.manufacturerName, a.modelName, a.serialNumber, a.assetClass]
          .filter(Boolean)
          .some((f) => (f as string).toLowerCase().includes(q)),
      )
    : assets;
  const highlighted = Math.min(hi, Math.max(0, filtered.length - 1));

  useEffect(() => { hiRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [highlighted, open]);

  function pick(a: AssetOpt) {
    if (isRackBlocked(a, rackOnly)) return;
    onChange(a.id); setOpen(false); setQuery('');
  }
  function clear() { onChange(''); setQuery(''); setOpen(true); setHi(0); }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(Math.min(highlighted + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(Math.max(highlighted - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const a = filtered[highlighted]; if (a) pick(a); }
    else if (e.key === 'Escape') { setOpen(false); }
  }

  const showList = open && !selected && filtered.length > 0;

  return (
    <div className="relative w-80">
      {selected ? (
        <div className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-brand-300 bg-brand-50 px-3 text-sm">
          <span className="truncate text-foreground">
            {assetLabel(selected)}
            <span className="ml-1.5 text-muted-foreground">· {selected.serialNumber ?? 'no serial'}</span>
          </span>
          <button
            type="button"
            aria-label="Clear selected asset"
            disabled={disabled}
            onClick={clear}
            className="flex-none rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <input
          value={query}
          disabled={disabled}
          role="combobox"
          aria-expanded={showList}
          aria-autocomplete="list"
          placeholder="Search assets by make, model, serial, or class…"
          className="h-9 w-full rounded-md border border-border px-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHi(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      )}
      {showList && (
        <div className="absolute left-0 z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-card py-1 shadow-md">
          {filtered.map((a, i) => {
            const blocked = isRackBlocked(a, rackOnly);
            return (
              <button
                type="button"
                key={a.id}
                ref={i === highlighted ? hiRef : undefined}
                disabled={blocked}
                aria-disabled={blocked}
                title={blocked ? RACK_SERVERS_ONLY_REASON : undefined}
                onMouseDown={(e) => { e.preventDefault(); pick(a); }}
                onMouseEnter={() => setHi(i)}
                className={cn(
                  'flex w-full flex-col gap-0.5 px-3 py-2 text-left',
                  i === highlighted && !blocked ? 'bg-brand-50' : '',
                  blocked ? 'cursor-not-allowed opacity-60' : '',
                )}
              >
                <span className="text-sm font-medium text-foreground">{assetLabel(a)}</span>
                <span className="text-xs text-muted-foreground">
                  {a.serialNumber ?? 'no serial'} · {a.assetClass}
                  {a.fmvMedianUsd != null && ` · ${formatUsd(a.fmvMedianUsd)}`}
                </span>
                {blocked && <span className="text-xs text-muted-foreground">{RACK_SERVERS_ONLY_REASON}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
